<template>
  <div :id="importSource.id" :data-navigation="importSource.name">
    <div
      class="flex items-center py-3 px-4 cursor-pointer hover:bg-slate-50 transition-colors border-t border-slate-200"
      @click="toggleExpanded"
    >
      <ImportFromSource
        :import-source="importSource"
        :icon-url="importSource.iconUrl"
      />
      <div class="flex-1 flex min-w-0">
        <span
          class="inline-flex min-w-8 h-8 px-2 items-center justify-center rounded-full text-sm font-medium text-blue-800 bg-blue-100"
        >
          {{ searchCount }}
        </span>
      </div>
      <div class="flex items-center flex-shrink-0">
        <div class="w-20 flex justify-center">
          <span
            class="inline-flex min-w-8 h-8 px-2 items-center justify-center rounded-full text-sm font-medium text-blue-800 bg-blue-100"
          >
            {{ totalResults }}
          </span>
        </div>
        <div class="flex items-center gap-2 w-16 justify-end">
          <ChevronDownIcon
            v-if="isExpanded"
            class="w-4 h-4 text-gray-800 transition-transform duration-200"
          />
          <ChevronRightIcon
            v-else
            class="w-4 h-4 text-gray-800 transition-transform duration-200"
          />
          <PlanLockGuard>
            <button
              v-if="
                (review.searchesBySource.value[importSource.id]?.length ?? 0) <=
                0
              "
              class="text-red-500 hover:text-red-600 p-1"
              :disabled="review.isPlanReadonly.value"
              @click.stop="removeImportSourceFromPlan(importSource.id)"
            >
              <TrashIcon class="w-4 h-4" />
            </button>
          </PlanLockGuard>
        </div>
      </div>
    </div>
    <div
      v-if="isExpanded && isSelected"
      class="bg-neutral-background-secondary border-t border-slate-100"
    >
      <div
        v-if="review.searchesBySource.value[importSource.id]?.length > 0"
        class="py-2 px-4"
      >
        <div
          class="flex items-center text-xs text-slate-900 border-b border-slate-200 bg-neutral-background-secondary py-2 pl-5 font-medium"
        >
          <span class="w-20 flex-shrink-0">Search</span>
          <span class="w-44 flex-shrink-0">Date</span>
          <span
            v-if="importSource.id === BuiltInImportSourceId.CITATION_SEARCH"
            class="w-20 flex-shrink-0"
          >
            Parent
          </span>
          <span
            v-if="
              importSource.id !== BuiltInImportSourceId.HAND_SEARCH &&
              importSource.id !== BuiltInImportSourceId.CITATION_SEARCH
            "
            class="flex-1 min-w-0"
          >
            Query
          </span>
          <span v-else class="flex-1 min-w-0"></span>
          <div class="flex items-center flex-shrink-0">
            <span class="w-20 text-center">Results</span>
            <span class="w-16"></span>
          </div>
        </div>
        <template
          v-for="(search, index) in getSourceSearches(importSource.id)"
          :key="importSource.id + index"
        >
          <SearchRow
            :search="search"
            :search-number="index + 1"
            :import-source="importSource"
          />
        </template>
      </div>
      <div
        v-else
        class="px-4 py-6 flex flex-col items-center text-slate-500 text-sm"
      >
        <SearchNotFoundIcon />
        <p
          v-if="
            review.entity.value.plan?.lockState !== ReviewLockState.LOCKED &&
            importSource.id === BuiltInImportSourceId.HAND_SEARCH
          "
        >
          You haven't imported any record yet.
        </p>
        <p v-else>You haven't imported any search yet.</p>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '@app/views/Review/use-review'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import { SnackbarState } from '@app/types'
import { Search } from '@core/domain/models/search.model'
import TrashIcon from '@app/components/Icons/TrashIcon.vue'
import PlanLockGuard from '@app/views/Review/Plan/PlanLockGuard.vue'
import { HttpException } from '@core/exceptions/http.exception'
import { errorMessage } from '@app/utils/error-message'
import { BuiltInImportSourceId } from '@core/domain/types/builtInImportSourceId'
import { computed, ref } from 'vue'
import { ImportSource } from '@core/domain/models/import-source.model'
import { ReviewLockState } from '@core/domain/types/reviewLockState.type'
import SearchRow from './ImportSourceCard/SearchRow.vue'
import ImportFromSource from './ImportFromSource.vue'
import SearchNotFoundIcon from '@app/components/Icons/SearchNotFoundIcon.vue'
import ChevronDownIcon from '@app/components/Icons/ChevronDownIcon.vue'
import ChevronRightIcon from '@app/components/Icons/ChevronRightIcon.vue'

const props = defineProps<{
  importSource: ImportSource
  iconUrl?: string
  query?: string
}>()

const loading = useLoading()
const snackbar = useSnackbar()

const review = injectStrict(ReviewKey)
const isExpanded = ref(false)

const isSelected = computed(() =>
  review.entity?.value?.plan?.importPlan?.importSources?.some(
    (ri) => ri.id === props.importSource.id,
  ),
)

const searchCount = computed(() => {
  return review.searchesBySource.value[props.importSource.id]?.length ?? 0
})

const totalResults = computed(() => {
  const searches = review.searchesBySource.value[props.importSource.id] ?? []
  return searches.reduce(
    (sum, search) => sum + (search.studies?.length ?? 0),
    0,
  )
})

const emit = defineEmits<(e: 'importSourceRemoved') => void>()

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
}

async function removeImportSourceFromPlan(sourceId: string) {
  try {
    loading.start()
    emit('importSourceRemoved')
    await review.removeImportSourceFromPlan(sourceId)
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
  } finally {
    loading.stop()
  }
}

function getSourceSearches(importSourceId: string): Search[] {
  return review.searchesBySource.value[importSourceId] ?? []
}
</script>
